import { differenceInMinutes, format, isAfter } from "date-fns"
import { AlertTriangle, Ban, Clock, Lock, MapPin, Truck, Unlock, User, UserX } from "lucide-react"

import { StatusPill } from "@/components/ui/status-pill"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { getDisplayRouteDelayMinutes } from "@/lib/routeDelayDisplay"
import {
  ROUTE_KIND_BADGE_CLASSES,
  getRouteKindBadge,
  getRouteKindRowClasses,
} from "@/lib/routeKindDisplay"
import {
  UNASSIGNED_WARNING_WINDOW_MINUTES,
  canCancelRoute,
  getRouteConcernScore,
  getRouteDueTime,
  getRouteStatusDisplay,
  hasGenuinelyStarted,
} from "@/lib/statusDisplay"
import { ROW_HEIGHT } from "@/lib/timelineScale"
import { type Variance, toneClasses, varianceClasses } from "@/lib/tone"
import { cn } from "@/lib/utils"
import type { Route } from "@/types/collections"

interface RouteLabelCellProps {
  route: Route
  isFirstStandbyRow: boolean
  hasUnsaved: boolean
  isHighlighted: boolean
  isToday: boolean
  isPastDate: boolean
  canEdit: boolean
  currentTime: Date
  loadFactor: number | undefined
  rowVariance: Variance | null
  onSelect: (routeId: string, event: React.MouseEvent) => void
  onToggleLock: (routeId: string) => void
  onCancelRoute: (route: Route, event: React.MouseEvent) => void
}

export default function RouteLabelCell({
  route,
  isFirstStandbyRow,
  hasUnsaved,
  isHighlighted: isHighlightedRoute,
  isToday,
  isPastDate,
  canEdit,
  currentTime,
  loadFactor,
  rowVariance,
  onSelect,
  onToggleLock,
  onCancelRoute,
}: RouteLabelCellProps) {
  const isStandbyRow = route.routeKind === "standby"

  const noDriver =
    !route.driverName ||
    route.driverName.trim() === "" ||
    route.driverName === "Unassigned"
  const routeDueTime = getRouteDueTime(route)
  const isLateStart =
    !isPastDate &&
    !hasGenuinelyStarted(route) &&
    !!routeDueTime &&
    isAfter(currentTime, routeDueTime)
  // Unassigned only earns alarm colour near departure (same window as getRouteAttentionIssues).
  const unassignedMatters =
    !isPastDate &&
    !!routeDueTime &&
    differenceInMinutes(routeDueTime, currentTime) <= UNASSIGNED_WARNING_WINDOW_MINUTES
  const concernScore = !isPastDate
    ? getRouteConcernScore(route, currentTime, loadFactor)
    : 0
  const routeStatus = getRouteStatusDisplay(route, currentTime)
  // Same smoothed band as the summary, so a route can't be red up top and plain here.
  const rowTone =
    rowVariance && rowVariance !== "done" && rowVariance !== "on_plan"
      ? varianceClasses(rowVariance)
      : null

  return (
    <div
      className={cn(
        "relative cursor-pointer overflow-hidden border-b border-palette-jungleGreen100 px-3 py-1.5 transition-colors",
        // A tinted row deepens its own colour on hover; the neutral hover would flash it white.
        rowTone
          ? ["border-l-4", rowTone.edge, rowTone.tint, rowTone.tintHover]
          : "hover:bg-palette-jungleGreen000",
        // After the variance tint (a spare has no variance worth colouring); transient states below
        // still win.
        isStandbyRow && getRouteKindRowClasses("standby"),
        isFirstStandbyRow && "border-t-2 border-t-slate-400",
        // Transient states the operator just caused win over standing ones.
        hasUnsaved && "border-l-4 border-l-amber-400 bg-amber-50/50",
        isHighlightedRoute && "animate-pulse border-l-4 border-l-amber-500 bg-amber-100",
        !isPastDate &&
          concernScore >= 50 &&
          !hasUnsaved &&
          !isHighlightedRoute &&
          "border-l-4 border-l-red-400",
        isPastDate &&
          getDisplayRouteDelayMinutes(route) >= 5 &&
          "border-l-4 border-l-red-500 bg-red-50/50",
        isPastDate &&
          getDisplayRouteDelayMinutes(route) <= -5 &&
          "border-l-4 border-l-green-500 bg-green-50/50"
      )}
      style={{ height: `${ROW_HEIGHT}px` }}
      onClick={(e) => {
        const target = e.target as HTMLElement
        const isButton = target.closest("button") !== null
        const isInteractive =
          target.closest('[role="button"]') !== null ||
          target.closest("a") !== null ||
          target.closest("[onclick]") !== null

        if (!isButton && !isInteractive) {
          onSelect(route.id, e)
        }
      }}
    >
      {isPastDate && <RoutePerformanceTooltip route={route} />}
      <div className="relative z-10 flex h-full flex-col justify-center gap-0.5">
        <div className="flex items-center justify-between gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex min-w-0 items-center gap-1.5">
                <div
                  className={cn(
                    "h-2 w-2 flex-shrink-0 rounded-full",
                    toneClasses(routeStatus.tone).dot
                  )}
                />
                <span className="truncate text-xs font-semibold text-palette-jungleGreen900">
                  {route.code}
                </span>
                {getRouteKindBadge(route.routeKind) && (
                  <span className={ROUTE_KIND_BADGE_CLASSES}>
                    {getRouteKindBadge(route.routeKind)}
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="font-mono text-xs">
              {route.code}
            </TooltipContent>
          </Tooltip>
          <div className="flex flex-shrink-0 items-center gap-0.5">
            {isToday && canEdit && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  onToggleLock(route.id)
                }}
                className={cn(
                  "rounded p-0.5 transition-colors",
                  route.isLocked
                    ? "text-palette-jungleGreen400 hover:bg-palette-jungleGreen100 hover:text-palette-jungleGreen600"
                    : "text-amber-500 hover:bg-amber-100 hover:text-amber-600"
                )}
              >
                {route.isLocked ? <Lock size={11} /> : <Unlock size={11} />}
              </button>
            )}
            {/* Only while unlocked, to avoid a permanent destructive mis-click target; the
                details panel always has it. */}
            {canCancelRoute(route).allowed && !route.isLocked && (
              <button
                onClick={(e) => onCancelRoute(route, e)}
                aria-label={`Cancel route ${route.code}`}
                className="rounded p-0.5 text-palette-jungleGreen400 transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <Ban size={11} />
              </button>
            )}
          </div>
        </div>
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden text-[10px] text-palette-jungleGreen500">
          {hasUnsaved ? (
            <span className="flex flex-shrink-0 items-center gap-0.5 rounded border border-amber-300 bg-amber-100 px-1 py-0.5 text-[9px] text-amber-700">
              <AlertTriangle size={7} />
              Unsaved
            </span>
          ) : (
            <StatusPill
              tone={routeStatus.tone}
              label={routeStatus.detailLabel}
              size="sm"
              className="flex-shrink-0 rounded"
            />
          )}
          <Clock size={8} className="flex-shrink-0 text-palette-jungleGreen400" />
          {isPastDate && route.actualStartTime ? (
            <span className="whitespace-nowrap">
              <span className="text-palette-jungleGreen400 line-through">
                {format(route.plannedStartTime, "HH:mm")}
              </span>{" "}
              <span className="font-medium text-emerald-600">
                {format(route.actualStartTime, "HH:mm")}
              </span>
            </span>
          ) : (
            <span className="whitespace-nowrap">
              {format(route.plannedStartTime, "HH:mm")}-
              {format(route.plannedEndTime, "HH:mm")}
            </span>
          )}
          <span className="flex-shrink-0 text-palette-jungleGreen300">•</span>
          <MapPin size={8} className="flex-shrink-0 text-palette-jungleGreen400" />
          <span className="whitespace-nowrap">
            {route.completedStops}/{route.totalStops}
          </span>
          {!isLateStart && !noDriver && (
            <RouteAlert route={route} isPastDate={isPastDate} loadFactor={loadFactor} />
          )}
        </div>
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden text-[10px] text-palette-jungleGreen500">
          <div className="flex min-w-0 flex-1 items-center gap-1">
            {!route.vehicleReg || route.vehicleReg.trim() === "" ? (
              <span
                className={cn(
                  "flex flex-shrink-0 items-center gap-0.5",
                  unassignedMatters
                    ? cn(toneClasses("danger").text, "font-semibold")
                    : "italic text-palette-jungleGreen400"
                )}
              >
                <Truck size={8} />
                No vehicle
              </span>
            ) : (
              <>
                <Truck size={8} className="flex-shrink-0 text-palette-jungleGreen400" />
                <span className="min-w-0 truncate">{route.vehicleReg}</span>
              </>
            )}
            <span className="flex-shrink-0 text-palette-jungleGreen300">•</span>
            {noDriver ? (
              <span
                className={cn(
                  "flex flex-shrink-0 items-center gap-0.5",
                  unassignedMatters
                    ? cn(toneClasses("danger").text, "font-semibold")
                    : "italic text-palette-jungleGreen400"
                )}
              >
                <UserX size={8} />
                No driver
              </span>
            ) : (
              <>
                <User size={8} className="flex-shrink-0 text-palette-jungleGreen400" />
                <span className="min-w-0 truncate">{route.driverName}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

function RouteAlert({
  route,
  isPastDate,
  loadFactor,
}: {
  route: Route
  isPastDate: boolean
  loadFactor: number | undefined
}) {
  if (isPastDate && getDisplayRouteDelayMinutes(route) !== 0) {
    return (
      <>
        <span className="flex-shrink-0 text-palette-jungleGreen300">•</span>
        <span
          className={cn(
            "whitespace-nowrap font-medium",
            toneClasses(
              getDisplayRouteDelayMinutes(route) > 0 ? "danger" : "success"
            ).text
          )}
        >
          {getDisplayRouteDelayMinutes(route) > 0 ? "+" : ""}
          {getDisplayRouteDelayMinutes(route)}m
        </span>
      </>
    )
  }
  if (loadFactor !== undefined && loadFactor >= 100) {
    return (
      <>
        <span className="flex-shrink-0 text-palette-jungleGreen300">•</span>
        <span
          className={cn(
            "whitespace-nowrap font-medium",
            toneClasses("danger").text
          )}
        >
          {loadFactor.toFixed(0)}%
        </span>
      </>
    )
  }
  return null
}

function RoutePerformanceTooltip({ route }: { route: Route }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="pointer-events-none absolute inset-0 z-0" />
      </TooltipTrigger>
      <TooltipContent side="right" className="max-w-xs">
        <div className="mb-2 text-xs font-semibold">Route Performance Summary</div>
        <div className="space-y-1.5 text-xs">
          <div className="flex justify-between">
            <span className="text-palette-jungleGreen500">Start Time:</span>
            <span>
              {route.actualStartTime ? (
                <>
                  <span className="mr-1 text-palette-jungleGreen400 line-through">
                    {format(route.plannedStartTime, "HH:mm")}
                  </span>
                  <span className="font-medium text-emerald-600">
                    {format(route.actualStartTime, "HH:mm")}
                  </span>
                  {route.actualStartTime.getTime() !==
                    route.plannedStartTime.getTime() && (
                    <span
                      className={cn(
                        "ml-1",
                        route.actualStartTime.getTime() >
                          route.plannedStartTime.getTime()
                          ? "text-red-600"
                          : "text-green-600"
                      )}
                    >
                      (
                      {route.actualStartTime.getTime() >
                      route.plannedStartTime.getTime()
                        ? "+"
                        : ""}
                      {Math.round(
                        (route.actualStartTime.getTime() -
                          route.plannedStartTime.getTime()) /
                          (1000 * 60)
                      )}
                      m)
                    </span>
                  )}
                </>
              ) : (
                <span>{format(route.plannedStartTime, "HH:mm")}</span>
              )}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-palette-jungleGreen500">End Time:</span>
            <span>
              {route.actualEndTime ? (
                <>
                  <span className="mr-1 text-palette-jungleGreen400 line-through">
                    {format(route.plannedEndTime, "HH:mm")}
                  </span>
                  <span className="font-medium text-emerald-600">
                    {format(route.actualEndTime, "HH:mm")}
                  </span>
                  {route.actualEndTime.getTime() !==
                    route.plannedEndTime.getTime() && (
                    <span
                      className={cn(
                        "ml-1",
                        route.actualEndTime.getTime() > route.plannedEndTime.getTime()
                          ? "text-red-600"
                          : "text-green-600"
                      )}
                    >
                      (
                      {route.actualEndTime.getTime() > route.plannedEndTime.getTime()
                        ? "+"
                        : ""}
                      {Math.round(
                        (route.actualEndTime.getTime() -
                          route.plannedEndTime.getTime()) /
                          (1000 * 60)
                      )}
                      m)
                    </span>
                  )}
                </>
              ) : (
                <span>{format(route.plannedEndTime, "HH:mm")}</span>
              )}
            </span>
          </div>
          {getDisplayRouteDelayMinutes(route) !== 0 && (
            <div className="flex justify-between">
              <span className="text-palette-jungleGreen500">Overall Delay:</span>
              <span
                className={cn(
                  "font-medium",
                  getDisplayRouteDelayMinutes(route) > 0
                    ? "text-red-600"
                    : "text-green-600"
                )}
              >
                {getDisplayRouteDelayMinutes(route) > 0 ? "+" : ""}
                {getDisplayRouteDelayMinutes(route)} minutes
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-palette-jungleGreen500">Stops Completed:</span>
            <span>
              {route.completedStops}/{route.totalStops}
            </span>
          </div>
          {route.distanceMiles && (
            <div className="flex justify-between">
              <span className="text-palette-jungleGreen500">Distance:</span>
              <span>{route.distanceMiles.toFixed(1)} miles</span>
            </div>
          )}
          {route.durationMinutes && (
            <div className="flex justify-between">
              <span className="text-palette-jungleGreen500">Duration:</span>
              <span>{Math.round((route.durationMinutes / 60) * 10) / 10} hours</span>
            </div>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  )
}
